import { getProductData } from "~/domains/product/types/ProductData";
import { getPrimaryImage } from "~/utils/product/getPrimaryImage";
import { ImageFormat } from "~/domains/product/types/ProductDataDTO";
import { Trans } from "react-i18next";
import { type AlternativeBannerContext } from "~/features/productDetails/components/PhaseInOut/usePhaseInOutLogic";
import CenterContents from "~/common/components/CenterContents";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { usePurchasePlace } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import type { PurchaseType } from "~/app/analytics/ga/recommendations/types";
import { appendPurchaseParams } from "~/features/productDetails/components/ProductSummary/PdpSummaryHelper";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import { Box, Stack, styled } from "~/common/mui";

const NotificationRoot = styled("a")(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: `3px 0 0 0 ${theme.palette.info.main} inset`,
  borderRadius: theme.spacing(1),
  background: theme.palette.surfaceContainer.lowest,
  color: theme.palette.text.primary,
}));

const ContentRow = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  gap: theme.spacing(2),
}));

const ImageSection = styled(Stack)(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  alignItems: "center",
  flex: "none",
  height: theme.spacing(8),
  width: theme.spacing(8),
}));

const BoxImage = styled("img")(({ theme }) => ({
  width: theme.spacing(12.5),
  height: theme.spacing(12.5),
  objectFit: "cover",
  objectPosition: "center",
}));

const HeadlineBig = styled("p")(({ theme }) => ({
  ...theme.typography.titleMedium,
  fontWeight: "bold",
  marginBottom: theme.spacing(0.5),
}));

const Description = styled("p")(({ theme }) => ({
  color: theme.palette.text.secondary,
  ...theme.typography.bodyMedium,
  margin: 0,
}));

const Arrow = styled("div")(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: theme.spacing(1.5),
}));

interface Props {
  className?: string;
  data: AlternativeBannerContext;
}

export const AlternativeNotification = (props: Props) => {
  const reco_place = usePurchasePlace();
  const reco_type: PurchaseType = "ap";

  if (props.data.status !== "loaded") {
    return (
      <CenterContents className={props.className}>
        <CircleSpinner />
      </CenterContents>
    );
  }
  const alternative = getProductData(props.data.product);
  const designation = alternative.designation;
  const pdpUrl = appendPurchaseParams(alternative.url, reco_place, reco_type);
  return (
    <NotificationRoot
      tracking-aux-aspr-type={"Alternative"}
      tracking-aux-aspr-name={alternative.designation}
      tracking-aux-aspr-id={alternative.code}
      href={pdpUrl}
      className={props.className}
    >
      <ContentRow direction="row">
        <ImageSection>
          <BoxImage
            src={getPrimaryImage(alternative.images, { imageFormat: ImageFormat.product })?.url ?? ""}
            alt={""}
          />
        </ImageSection>
        <Box>
          <HeadlineBig>
            <Trans i18nKey={"page.pdp.phaseinout.alternative.headline"}></Trans>
          </HeadlineBig>

          <Description>
            <Trans i18nKey={"page.pdp.phaseinout.alternative.description"} designation={designation}>
              <b>{{ designation: designation }}</b>
            </Trans>
          </Description>
        </Box>
        <Arrow>
          <ArrowForwardOutlinedIcon />
        </Arrow>
      </ContentRow>
    </NotificationRoot>
  );
};
